import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { FaPlus, FaEdit, FaTrash, FaGlobe, FaCheckCircle, FaExclamationTriangle, FaBan, FaSearch, FaSync, FaChevronLeft, FaChevronRight, FaTimes } from 'react-icons/fa';
import axiosInstance from '../utils/axiosInstance';
import { toast } from 'react-toastify';

const STATUS = {
  active:  { label:'Aktif',      color:'#10b981', bg:'rgba(16,185,129,.09)',  border:'rgba(16,185,129,.2)',  icon:<FaCheckCircle /> },
  pending: { label:'Beklemede',  color:'#f59e0b', bg:'rgba(245,158,11,.09)',  border:'rgba(245,158,11,.2)',  icon:<FaExclamationTriangle /> },
  blocked: { label:'Engellendi', color:'#ef4444', bg:'rgba(239,68,68,.09)',   border:'rgba(239,68,68,.2)',   icon:<FaBan /> },
};

const DomainSettings = () => {
  const [domains,      setDomains]      = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [searchTerm,   setSearchTerm]   = useState('');
  const [isModalOpen,  setIsModalOpen]  = useState(false);
  const [editingItem,  setEditingItem]  = useState(null);
  const [formData,     setFormData]     = useState({ domainUrl:'', sequenceNumber:1, status:'pending' });
  const [currentPage,  setCurrentPage]  = useState(1);
  const PER_PAGE = 20;

  const fetchDomains = async () => {
    setLoading(true);
    try { const r = await axiosInstance.get('/domain/all'); setDomains(r.data); }
    catch { toast.error('Domainler yüklenemedi!'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchDomains(); }, []);
  useEffect(() => { setCurrentPage(1); }, [searchTerm]);

  const openModal = (domain=null) => {
    if (domain) { setEditingItem(domain); setFormData({ domainUrl:domain.domainUrl, sequenceNumber:domain.sequenceNumber, status:domain.status }); }
    else { const max = domains.length ? Math.max(...domains.map(d=>d.sequenceNumber||0)) : 0; setEditingItem(null); setFormData({ domainUrl:'', sequenceNumber:max+1, status:'pending' }); }
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = { ...formData, sequenceNumber:parseInt(formData.sequenceNumber) };
    try {
      if (editingItem) { await axiosInstance.patch(`/domain/update/${editingItem._id}`, data); toast.success('Domain güncellendi.'); }
      else             { await axiosInstance.post('/domain/add', data); toast.success('Domain eklendi.'); }
      setIsModalOpen(false); fetchDomains();
    } catch (e) { toast.error(e.response?.data?.error || 'İşlem başarısız.'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu domaini silmek istediğinize emin misiniz?')) return;
    try { await axiosInstance.delete(`/domain/delete/${id}`); toast.success('Domain silindi.'); fetchDomains(); }
    catch { toast.error('Silme başarısız.'); }
  };

  const sorted   = [...domains].sort((a,b) => ({active:1,blocked:2,pending:3}[a.status]||3) - ({active:1,blocked:2,pending:3}[b.status]||3));
  const filtered = sorted.filter(d => {
    const s = searchTerm.toLowerCase();
    return d.domainUrl.toLowerCase().includes(s) || d.status.includes(s) || (STATUS[d.status]?.label.toLowerCase()||'').includes(s);
  });
  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const items      = filtered.slice((currentPage-1)*PER_PAGE, currentPage*PER_PAGE);

  const inp = { width:'100%', padding:'10px 13px', background:'#0d0e12', border:'1px solid rgba(255,255,255,.08)', borderRadius:10, color:'#f1f5f9', fontSize:'.83rem', fontFamily:"'Outfit',sans-serif", outline:'none', transition:'all .2s' };

  return (
    <div style={{ minHeight:'100vh', background:'#090a0c', padding:'28px 28px 40px', color:'#e2e8f0', fontFamily:"'Outfit','Inter',sans-serif", boxSizing:'border-box' }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap');
        *{box-sizing:border-box}
        .dm-row:hover{background:rgba(255,255,255,.03)!important;transform:translateX(2px)}
        .dm-btn:hover{filter:brightness(1.2);transform:scale(1.07)}
        .dm-inp:focus{border-color:#f27127!important;box-shadow:0 0 0 3px rgba(242,113,39,.1)!important}
        @keyframes fadeUp{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}
        .dm-fade{animation:fadeUp .3s ease both}
      `}</style>

      {/* Header */}
      <div className="dm-fade" style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:24 }}>
        <div style={{ display:'flex', alignItems:'center', gap:14 }}>
          <div style={{ width:44, height:44, borderRadius:13, background:'rgba(6,182,212,.1)', border:'1px solid rgba(6,182,212,.25)', display:'flex', alignItems:'center', justifyContent:'center', color:'#06b6d4', fontSize:'1.1rem', boxShadow:'0 0 18px rgba(6,182,212,.1)' }}><FaGlobe /></div>
          <div>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <h2 style={{ margin:0, fontSize:'1.2rem', fontWeight:700, color:'#f8fafc', letterSpacing:'-.3px' }}>Domain Yönetimi</h2>
              <span style={{ padding:'2px 10px', borderRadius:20, background:'rgba(6,182,212,.1)', color:'#06b6d4', border:'1px solid rgba(6,182,212,.2)', fontSize:'.7rem', fontWeight:700 }}>{domains.length}</span>
            </div>
            <p style={{ margin:'3px 0 0', fontSize:'.71rem', color:'rgba(255,255,255,.22)' }}>Aktif ve engellenmiş domainlerin takibi</p>
          </div>
        </div>
        <div style={{ display:'flex', gap:9 }}>
          <button onClick={fetchDomains} style={{ display:'flex', alignItems:'center', gap:7, padding:'9px 14px', borderRadius:10, border:'1px solid rgba(255,255,255,.08)', background:'rgba(255,255,255,.04)', color:'rgba(255,255,255,.45)', fontSize:'.79rem', fontWeight:600, cursor:'pointer', fontFamily:'inherit', transition:'all .15s' }}>
            <FaSync style={{ fontSize:'.7rem', animation:loading?'spin 1s linear infinite':'none' }} /> Yenile
          </button>
          <button onClick={() => openModal()} style={{ display:'flex', alignItems:'center', gap:7, padding:'9px 16px', borderRadius:10, border:'none', background:'linear-gradient(135deg,#06b6d4,#0891b2)', color:'#fff', fontSize:'.79rem', fontWeight:700, cursor:'pointer', fontFamily:'inherit', boxShadow:'0 4px 14px rgba(6,182,212,.25)' }}>
            <FaPlus /> Yeni Ekle
          </button>
        </div>
      </div>

      {/* Arama */}
      <div style={{ position:'relative', marginBottom:18 }}>
        <FaSearch style={{ position:'absolute', left:14, top:'50%', transform:'translateY(-50%)', color:'rgba(255,255,255,.25)', fontSize:'.85rem', pointerEvents:'none' }} />
        <input className="dm-inp" type="text" placeholder="Domain veya durum ara..." value={searchTerm} onChange={e=>setSearchTerm(e.target.value)}
          style={{ ...inp, paddingLeft:40, background:'#111318', border:'1px solid rgba(255,255,255,.07)' }} />
      </div>

      {/* Tablo kartı */}
      <div style={{ background:'#111318', border:'1px solid rgba(255,255,255,.06)', borderRadius:20, overflow:'hidden' }}>
        <div style={{ display:'grid', gridTemplateColumns:'70px 1fr 140px 160px 100px', padding:'12px 18px', borderBottom:'1px solid rgba(255,255,255,.05)', background:'rgba(0,0,0,.2)' }}>
          {['SIRA','DOMAİN URL','DURUM','SON KONTROL','İŞLEMLER'].map((h,i) => (
            <div key={i} style={{ fontSize:'.59rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'1.2px', color:'rgba(255,255,255,.2)', textAlign:i===4?'center':'left' }}>{h}</div>
          ))}
        </div>

        <div style={{ padding:'6px 8px' }}>
          {loading ? (
            Array.from({length:5}).map((_,i) => <div key={i} style={{ height:48, borderRadius:12, marginBottom:4, background:'rgba(255,255,255,.03)' }} />)
          ) : items.length > 0 ? items.map(item => {
            const st = STATUS[item.status] || STATUS.pending;
            return (
              <div key={item._id} className="dm-row" style={{ display:'grid', gridTemplateColumns:'70px 1fr 140px 160px 100px', alignItems:'center', padding:'11px 10px', borderRadius:12, marginBottom:4, background:'rgba(255,255,255,.015)', border:'1px solid rgba(255,255,255,.03)', position:'relative', overflow:'hidden', transition:'all .15s' }}>
                <div style={{ position:'absolute', left:0, top:'15%', bottom:'15%', width:3, borderRadius:'0 3px 3px 0', background:st.color, boxShadow:`0 0 8px ${st.color}60` }} />
                <div style={{ paddingLeft:8 }}>
                  <span style={{ fontSize:'.74rem', fontWeight:700, color:'rgba(255,255,255,.3)' }}>#{item.sequenceNumber}</span>
                </div>
                <div>
                  <span style={{ fontSize:'.83rem', fontWeight:600, color:'#e2e8f0', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', display:'block' }}>{item.domainUrl}</span>
                </div>
                <div>
                  <span style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'3px 10px', borderRadius:7, fontSize:'.62rem', fontWeight:700, textTransform:'uppercase', letterSpacing:'.4px', background:st.bg, color:st.color, border:`1px solid ${st.border}` }}>
                    <span style={{ fontSize:'.58rem' }}>{st.icon}</span>{st.label}
                  </span>
                </div>
                <div style={{ fontSize:'.75rem', color:'rgba(255,255,255,.3)' }}>
                  {item.lastCheck ? dayjs(item.lastCheck).format('DD MMM HH:mm') : <span style={{ color:'rgba(255,255,255,.15)' }}>Kontrol edilmedi</span>}
                </div>
                <div style={{ display:'flex', gap:8, justifyContent:'center' }}>
                  <button className="dm-btn" onClick={() => openModal(item)} style={{ width:32, height:32, borderRadius:9, border:'none', cursor:'pointer', background:'rgba(99,102,241,.15)', color:'#818cf8', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'.78rem', transition:'all .15s' }}><FaEdit /></button>
                  <button className="dm-btn" onClick={() => handleDelete(item._id)} style={{ width:32, height:32, borderRadius:9, border:'none', cursor:'pointer', background:'rgba(239,68,68,.12)', color:'#f87171', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'.78rem', transition:'all .15s' }}><FaTrash /></button>
                </div>
              </div>
            );
          }) : (
            <div style={{ textAlign:'center', padding:'50px 0', color:'rgba(255,255,255,.15)', fontSize:'.85rem' }}>
              <FaGlobe style={{ fontSize:'2rem', opacity:.15, display:'block', margin:'0 auto 10px' }} />Domain bulunamadı
            </div>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'14px 18px', borderTop:'1px solid rgba(255,255,255,.05)', background:'rgba(0,0,0,.15)' }}>
            <span style={{ fontSize:'.72rem', color:'rgba(255,255,255,.2)' }}>Sayfa <b style={{ color:'rgba(255,255,255,.45)' }}>{currentPage}</b> / {totalPages}</span>
            <div style={{ display:'flex', gap:6 }}>
              <button disabled={currentPage===1} onClick={() => setCurrentPage(p=>p-1)} style={{ width:32, height:32, borderRadius:8, border:'1px solid rgba(255,255,255,.08)', background:'rgba(255,255,255,.04)', color:'rgba(255,255,255,.3)', cursor:currentPage===1?'not-allowed':'pointer', display:'flex', alignItems:'center', justifyContent:'center', opacity:currentPage===1?.35:1 }}><FaChevronLeft style={{ fontSize:'.65rem' }} /></button>
              <button disabled={currentPage===totalPages} onClick={() => setCurrentPage(p=>p+1)} style={{ width:32, height:32, borderRadius:8, border:'1px solid rgba(255,255,255,.08)', background:'rgba(255,255,255,.04)', color:'rgba(255,255,255,.3)', cursor:currentPage===totalPages?'not-allowed':'pointer', display:'flex', alignItems:'center', justifyContent:'center', opacity:currentPage===totalPages?.35:1 }}><FaChevronRight style={{ fontSize:'.65rem' }} /></button>
            </div>
          </div>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div onClick={() => setIsModalOpen(false)} style={{ position:'fixed', inset:0, zIndex:10000, background:'rgba(0,0,0,.8)', backdropFilter:'blur(10px)', display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}>
          <div onClick={e=>e.stopPropagation()} style={{ width:'100%', maxWidth:440, background:'#111318', border:`1px solid ${editingItem?'rgba(99,102,241,.2)':'rgba(6,182,212,.2)'}`, borderRadius:22, overflow:'hidden', boxShadow:'0 32px 80px rgba(0,0,0,.7)' }}>
            <div style={{ padding:'20px 24px 16px', borderBottom:'1px solid rgba(255,255,255,.05)', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
              <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                <div style={{ width:38, height:38, borderRadius:10, background:editingItem?'rgba(99,102,241,.12)':'rgba(6,182,212,.12)', border:`1px solid ${editingItem?'rgba(99,102,241,.25)':'rgba(6,182,212,.25)'}`, display:'flex', alignItems:'center', justifyContent:'center', color:editingItem?'#818cf8':'#06b6d4' }}>
                  {editingItem ? <FaEdit /> : <FaPlus />}
                </div>
                <div>
                  <div style={{ fontWeight:700, fontSize:'.92rem', color:'#f8fafc' }}>{editingItem ? 'Domain Düzenle' : 'Yeni Domain Ekle'}</div>
                  <div style={{ fontSize:'.68rem', color:'rgba(255,255,255,.25)', marginTop:2 }}>{editingItem ? editingItem.domainUrl : 'Yeni domain ekleyin'}</div>
                </div>
              </div>
              <button onClick={() => setIsModalOpen(false)} style={{ width:30, height:30, borderRadius:8, border:'none', cursor:'pointer', background:'rgba(255,255,255,.06)', color:'rgba(255,255,255,.35)', display:'flex', alignItems:'center', justifyContent:'center' }}><FaTimes /></button>
            </div>
            <form onSubmit={handleSubmit} style={{ padding:'20px 24px 24px' }}>
              <div style={{ marginBottom:14 }}>
                <label style={{ display:'block', fontSize:'.63rem', fontWeight:600, textTransform:'uppercase', letterSpacing:'1px', color:'rgba(255,255,255,.26)', marginBottom:6 }}>Domain URL</label>
                <input className="dm-inp" type="text" value={formData.domainUrl} onChange={e=>setFormData({...formData,domainUrl:e.target.value})} placeholder="example.com" required style={inp} />
              </div>
              <div style={{ marginBottom:14 }}>
                <label style={{ display:'block', fontSize:'.63rem', fontWeight:600, textTransform:'uppercase', letterSpacing:'1px', color:'rgba(255,255,255,.26)', marginBottom:6 }}>Sıralama No</label>
                <input type="number" value={formData.sequenceNumber} readOnly style={{ ...inp, opacity:.45, cursor:'not-allowed' }} />
              </div>
              {editingItem && (
                <div style={{ marginBottom:20 }}>
                  <label style={{ display:'block', fontSize:'.63rem', fontWeight:600, textTransform:'uppercase', letterSpacing:'1px', color:'rgba(255,255,255,.26)', marginBottom:6 }}>Durum</label>
                  <select className="dm-inp" value={formData.status} onChange={e=>setFormData({...formData,status:e.target.value})} style={{ ...inp, cursor:'pointer' }}>
                    <option value="active">Aktif</option>
                    <option value="pending">Beklemede</option>
                    <option value="blocked">Engellendi</option>
                  </select>
                </div>
              )}
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginTop:editingItem?0:20 }}>
                <button type="button" onClick={() => setIsModalOpen(false)} style={{ padding:'11px', borderRadius:11, border:'1px solid rgba(255,255,255,.08)', background:'rgba(255,255,255,.05)', color:'rgba(255,255,255,.5)', fontSize:'.82rem', fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>İptal</button>
                <button type="submit" style={{ padding:'11px', borderRadius:11, border:'none', background:'linear-gradient(135deg,#06b6d4,#0891b2)', color:'#fff', fontSize:'.82rem', fontWeight:700, cursor:'pointer', fontFamily:'inherit', boxShadow:'0 4px 14px rgba(6,182,212,.22)' }}>{editingItem ? 'Güncelle' : 'Sisteme Ekle'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
export default DomainSettings;
